var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, html, LitElement } from "lit";
import { property } from "lit/decorators.js";
import "../icon/icon.js";
import { detectLocale } from "../i18n.js";
import { reset } from "../styles.js";
import { styles } from "./styles.js";
const alertVariants = {
    negative: "negative",
    positive: "positive",
    warning: "warning",
    info: "info",
};
/**
 * Alert is an inline component used for displaying different types of messages.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/alert/frameworks/elements)
 */
class WarpAlert extends LitElement {
    constructor() {
        super();
        /**
         * @summary Visual style of the alert.
         *
         * @description Controls both the color treatment and icon used by the component. Accepted values are `info`, `warning`, `positive`, and `negative`. Use the variant that matches the message severity so users can quickly distinguish informative messages from warnings, errors, and confirmations.
         */
        this.variant = "info";
        /**
         * @summary Whether the alert is visible.
         *
         * @description Alerts are hidden by default (`false`). Set this to `true` to render and expand the content. This is reflected as an attribute, so visibility can be controlled from markup (`show`) or from JavaScript (`element.show = true`).
         */
        this.show = false;
        /**
         * @summary ARIA role announced to assistive technology.
         *
         * @description Defaults to `alert` so urgent changes are announced by screen readers. Override this only when your use case requires a different announcement behavior, for example a less assertive live region strategy.
         */
        this.role = "alert";
        this._internals = this.attachInternals();
        // Use ElementInternals for ARIA to avoid hydration mismatches
        this._internals.role = "alert";
        // Don't set this.show here - let the attribute or explicit property set control it
        // Alerts default to hidden and are shown via the show attribute
    }
    connectedCallback() {
        super.connectedCallback();
        // Validate variant if explicitly set
        if (this.variant && !alertVariants[this.variant]) {
            throw new Error(`Invalid 'variant' attribute. Set its value to one of the following:\nnegative, positive, warning, info.`);
        }
    }
    // Slotted elements remain in lightDOM which allows for control of their style outside of shadowDOM.
    // ::slotted([Simple Selector]) confirms to Specificity rules, but (being simple) does not add weight to lightDOM skin selectors,
    // so never gets higher Specificity. Thus in order to overwrite style linked within shadowDOM, we need to use !important.
    // https://stackoverflow.com/a/61631668
    static { this.styles = [
        reset,
        styles,
        css `
			:host {
				display: block;
			}

			::slotted(:first-child) {
				margin-top: 0px;
			}

			::slotted(:last-child) {
				margin-bottom: 0px !important;
			}
		`,
    ]; }
    /** @internal */
    get _icon() {
        const locale = detectLocale();
        const variant = this.variant || "info";
        if (variant === alertVariants.info) {
            return html `<w-icon
				name="Info"
				size="small"
				locale="${locale}"
			></w-icon>`;
        }
        if (variant === alertVariants.warning) {
            return html `<w-icon
				name="Warning"
				size="small"
				locale="${locale}"
			></w-icon>`;
        }
        if (variant === alertVariants.negative) {
            return html `<w-icon
				name="Error"
				size="small"
				locale="${locale}"
			></w-icon>`;
        }
        if (variant === alertVariants.positive) {
            return html `<w-icon
				name="Success"
				size="small"
				locale="${locale}"
			></w-icon>`;
        }
        return "";
    }
    render() {
        return html `
			<w-expand-transition ?show=${this.show}>
				<div role=${this.role} part="base">
					<div part="icon">${this._icon}</div>
					<div part="content">
						<slot></slot>
					</div>
				</div>
			</w-expand-transition>
		`;
    }
}
__decorate([
    property({ reflect: true, useDefault: true })
], WarpAlert.prototype, "variant", void 0);
__decorate([
    property({ type: Boolean, reflect: true, useDefault: true })
], WarpAlert.prototype, "show", void 0);
__decorate([
    property({ reflect: true, useDefault: true })
], WarpAlert.prototype, "role", void 0);
if (!customElements.get("w-alert")) {
    customElements.define("w-alert", WarpAlert);
}
export { WarpAlert };
//# sourceMappingURL=alert.js.map